import { ArrowLeft, CheckCircle2, ExternalLink, FileText, Loader2 } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { completeLearningResource, getLearningResource } from '../../api.js'
import { markdownToSafeHtml } from '../../security/sanitize-html.js'

function fallbackResource(resourceId) {
  return {
    id: resourceId,
    title: '学习文档',
    type: 'document',
    content: '',
    platform: '站内文档',
    duration: '15 分钟',
    completed: false,
  }
}

function LearningResourceDetailPage() {
  const { resourceId, id } = useParams()
  const currentId = resourceId || id
  const [resource, setResource] = useState(() => fallbackResource(currentId))
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  useEffect(() => {
    const controller = new AbortController()
    setLoading(true)
    getLearningResource(currentId, controller.signal)
      .then((payload) => {
        setResource(payload?.resource || fallbackResource(currentId))
        setError('')
      })
      .catch((err) => {
        if (err.name === 'AbortError') return
        setResource(fallbackResource(currentId))
        setError(err.message || '学习资源暂时加载失败。')
      })
      .finally(() => setLoading(false))
    return () => controller.abort()
  }, [currentId])

  const markdown = String(resource.markdown || resource.content || resource.body || '').trim()
  const html = useMemo(() => (
    markdown ? markdownToSafeHtml(markdown) : ''
  ), [markdown])

  async function handleComplete() {
    if (resource.completed) return
    setSaving(true)
    setError('')
    setNotice('')
    try {
      const payload = await completeLearningResource(resource.id)
      setResource(payload?.resource || { ...resource, completed: true })
      setNotice(payload?.message || '阅读记录已写入成长中心。')
    } catch (err) {
      setError(err.message || '保存失败，请登录后重试。')
    } finally {
      setSaving(false)
    }
  }

  return (
    <main className="page-container student-roadmap-page learning-resource-detail-page">
      <section className="student-roadmap-stage-toolbar">
        <Link className="secondary-button" to={resource.nodeId ? `/learning/${resource.nodeId}` : '/learning'}>
          <ArrowLeft size={16} /> 返回学习节点
        </Link>
        {resource.url ? (
          <a className="secondary-button" href={resource.url} target="_blank" rel="noreferrer">
            外部资料 <ExternalLink size={16} />
          </a>
        ) : null}
      </section>

      <section className="student-roadmap-stage-hero learning-os-stage-hero">
        <div>
          <span><FileText size={18} /> {resource.platform || '站内文档'} · {resource.duration || '15 分钟'}</span>
          <h1>{loading ? '正在加载文档...' : resource.title || '学习文档'}</h1>
          <p>{resource.nodeTitle ? `所属节点：${resource.nodeTitle}` : '阅读完成后可以写入成长中心。'}</p>
        </div>
        <button className="primary-button" type="button" disabled={saving || resource.completed || loading} onClick={handleComplete}>
          {saving ? <Loader2 size={16} /> : <CheckCircle2 size={16} />}
          {resource.completed ? '已完成阅读' : '阅读完成'}
        </button>
      </section>

      {error ? (
        <div className="student-growth-error">
          <strong>文档加载失败</strong>
          <p>{error}</p>
          <Link className="secondary-button" to="/learning">返回学习路线</Link>
        </div>
      ) : null}
      {notice ? <p className="student-growth-error is-success">{notice}</p> : null}

      {html ? (
        <article className="learning-resource-markdown" dangerouslySetInnerHTML={{ __html: html }} />
      ) : (
        <article className="learning-resource-markdown">
          <h2>{resource.title || '暂无文档内容'}</h2>
          <p>这个资源还没有配置正文。你可以先返回学习节点，继续完成视频或练习任务。</p>
        </article>
      )}
    </main>
  )
}

export default LearningResourceDetailPage
